import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  FormHelperText,
  FormControlLabel,
  Checkbox,
  Grid as Grid2,
} from "@mui/material";
import {
  useCreateAndUpdateServiceFeeMutation,
  useGetModuleListQuery,
  useLazyGetModuleFieldsQuery,
  useGetServiceFeeByIdQuery,
} from "../../../../Services/ServiceFee";
import { FaPlus, FaMinus, FaXmark } from "react-icons/fa6";

import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { showSnackbar } from "../../../../Redux/Slices/NotificationSlice";
import { useDispatch } from "react-redux";
import Breadcrumb from "../../../../Components/BreadCrumb";
import Utils from "../../../../Helpers/Utils";

const operators = ["=", "!=", ">", "<", "<=", ">="];
const nextConditions = ["AND", "OR", "NOT", "None"];

interface ServiceFeeRule {
  _id?: string;
  isActive: boolean;
  description?: string;
  conditions?: any;
  outputType?: string;
  outputValue?: number;
  vatOnServiceFee?: string;
  priority?: number;
  ruleName?: string;
}

interface ServiceFeeFormInputs {
  profileName: string;
  profileCode: string;
  isActive: boolean;
  description: string;
  validFrom: string;
  validTo: string;
  waveOff: boolean;
  vatOnServiceFee?: string;
  promotedFrom: string;
  promotedTo: string;
  rules: ServiceFeeRule[];
}

interface ServiceFeeFormErrors {
  rules?: Array<{
    conditions?: {
      amount?: string;
      minAmount?: string;
      maxAmount?: string;
      discountPercentage?: string;
      countryTaxId?: string;
      additionalFee?: string;
      startTime?: string;
      endTime?: string;
      multiplier?: string;
      membershipType?: string;
      exemptionAmount?: string;
    };
  }>;
  validFrom?: string;
  validTo?: string;
  validToBeforeValidFrom?: boolean;
  promotedOutsideValidRange?: boolean;
}
interface Condition {
  module: string;
  field: string;
  value: any;
  operator: string;
  nextCondition: string;
}

const AddServiceFee: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [createAndUpdateServiceFee, { isLoading }] =
    useCreateAndUpdateServiceFeeMutation();
  const { id } = useParams();

  const { data: moduleList, isLoading: isModuleLoading } =
    useGetModuleListQuery();

  const { data: serviceFeeDetails } = useGetServiceFeeByIdQuery(id ?? "", {
    skip: !id,
  });

  const scrollRef = React.useRef<HTMLDivElement | null>(null);

  const [rulesList, setRulesList] = useState<any[]>([]);
  const [ruleName, setRuleName] = useState("");
  const [priority, setPriority] = useState(0);
  const [validFrom, setValidFrom] = useState("");
  const [validTo, setValidTo] = useState("");
  const [validDateError, setValidDateError] = useState({
    validToBeforeValidFrom: false,
    promotedOutsideValidRange: false,
  });
  const [showDateErrors, setShowDateErrors] = useState(false);
  const [outputValue, setOutputValue] = useState("");
  const [outputType, setOutputType] = useState("");
  const [editIndex, setEditIndex] = useState<number | null>(null);

  const [vatOnServiceFee, setVatOnServiceFee] = useState("");
  const [waveOff, setWaveOff] = useState(false);
  const [promotedFrom, setPromotedFrom] = useState("");
  const [promotedTo, setPromotedTo] = useState("");
  const [promotedDateError, setPromotedDateError] = useState(false);
  const [showPromotedDateErrors, setShowPromotedDateErrors] = useState(false);

  const [rowModuleFields, setRowModuleFields] = useState<{
    [key: string]: any;
  }>({});
  const [fetchModuleFields] = useLazyGetModuleFieldsQuery();

  const getRowModuleFields = (moduleId: string) => {
    return rowModuleFields[moduleId] || [];
  };

  const [modules, setModules] = useState<string[]>([]);
  useEffect(() => {
    if (moduleList && !isModuleLoading) {
      setModules(moduleList?.data || []);
    }
  }, [moduleList, isModuleLoading]);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ServiceFeeFormInputs, ServiceFeeFormErrors>({
    defaultValues: {
      profileName: "",
      profileCode: "",
      isActive: true,
      promotedFrom: "",
      promotedTo: "",
      waveOff: false,
      description: "",
      rules: [],
      vatOnServiceFee: "",
    },
  });

  useEffect(() => {
    if (id && serviceFeeDetails?.data) {
      // Patch all form fields with existing service fee data
      setValue("profileName", serviceFeeDetails.data.profileName);
      setValue("profileCode", serviceFeeDetails.data.profileCode);
      setValue("isActive", serviceFeeDetails.data.isActive);
      setValue("description", serviceFeeDetails.data.description);
      setValidFrom(formatDate(serviceFeeDetails.data.validFrom));
      setValidTo(formatDate(serviceFeeDetails.data.validTo));
      setValue("rules", serviceFeeDetails.data.rules || []);
      setRulesList(serviceFeeDetails.data.rules || []);
      setValue("waveOff", serviceFeeDetails.data.waveOff || false);
      setValue("promotedFrom", formatDate(serviceFeeDetails.data.promotedFrom));
      setValue("promotedTo", formatDate(serviceFeeDetails.data.promotedTo));
      setValue("vatOnServiceFee", serviceFeeDetails.data.vatOnServiceFee);
      setPromotedFrom(formatDate(serviceFeeDetails.data.promotedFrom));
      setPromotedTo(formatDate(serviceFeeDetails.data.promotedTo));

      setWaveOff(serviceFeeDetails.data.waveOff);
      setVatOnServiceFee(serviceFeeDetails.data.vatOnServiceFee);
    }
  }, [id, serviceFeeDetails, setValue, setRulesList]);

  // Utility to get today's date in YYYY-MM-DD format
  const getFormattedDate = (date: Date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const today = new Date();
  const todayDate = getFormattedDate(today);

  // Create tomorrow by adding 1 day
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const tomorrowDate = getFormattedDate(tomorrow);

  const breadcrumbItems = [
    { label: "Service Fee List", path: "service-fee" },
    { label: id ? "Edit Service Fee Profile" : "Add Service Fee Profile" },
  ];

  const [rows, setRows] = useState([
    {
      id: Date.now(),
      module: "",
      field: "",
      value: "",
      operator: "",
      nextCondition: "",
      disableValue: false,
      disableOperator: false,
    },
  ]);

  const addRow = () => {
    setRows([
      ...rows,
      {
        id: Date.now(),
        module: "",
        field: "",
        value: "",
        operator: "",
        nextCondition: "",
        disableValue: false,
        disableOperator: false,
      },
    ]);
  };

  const removeRow = (id: number) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const handleRowChange = async (
    id: number,
    fieldKey: string,
    fieldValue: any,
  ) => {
    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.id !== id) return row;

        // Determine updated module and field based on this change
        const newModule = fieldKey === "module" ? fieldValue : row.module;
        const newField = fieldKey === "field" ? fieldValue : row.field;

        const shouldDisableValue =
          newModule === "PaymentMethods" &&
          (newField === "Online" || newField === "BankTransfer");

        const shouldDisableOperator =
          newField === "Online" || newField === "BankTransfer";

        // Auto-fill 'Total' field when 'Order' is selected as the module
        const shouldAutoFillTotal =
          fieldKey === "module" && fieldValue === "Orders";

        return {
          ...row,
          [fieldKey]: fieldValue,
          ...(shouldDisableValue && {
            value: "",
            operator: "=",
            disableValue: true,
          }),
          ...(!shouldDisableValue && {
            disableValue: false,
            // Only update field if we're changing the module to 'Order' and it's not already set
            ...(shouldAutoFillTotal && { field: "Total" }),
          }),
          ...(shouldDisableOperator
            ? { operator: "=", disableOperator: true }
            : { disableOperator: false }),
        };
      }),
    );

    if (fieldKey === "module") {
      if (!rowModuleFields[fieldValue]) {
        try {
          const res = await fetchModuleFields(fieldValue).unwrap();
          setRowModuleFields((prev) => ({
            ...prev,
            [fieldValue]: res.data,
          }));
        } catch (error) {}
      }
    }
  };

  const consolidateRuleData = () => {
    const consolidatedRules = rulesList.map((rule) => ({
      ruleName: rule.ruleName,
      priority: rule.priority,
      outputValue: rule.outputValue,
      outputType: rule.outputType,
      conditions: rule.conditions.map((cond: Condition) => ({
        module: cond.module,
        field: cond.field,
        value: cond.value,
        operator: cond.operator,
        nextCondition: cond.nextCondition,
      })),
    }));

    return consolidatedRules;
  };

  const resetForm = () => {
    setValue("profileName", "");
    setValue("profileCode", "");
    setValue("description", "");
    setValue("isActive", true);
    setValue("rules", [
      {
        isActive: true,
        outputType: "",
        outputValue: 0,
        conditions: [],
      },
    ]);
    setValue("waveOff", false);
    setValue("promotedFrom", "");
    setValue("promotedTo", "");
    setValue("vatOnServiceFee", "");
    setRulesList([]);
    setRuleName("");
    setPriority(0);
    setOutputValue("");
    setOutputType("");
    setValidFrom("");
    setValidTo("");
    setPromotedFrom("");
    setPromotedTo("");
    setWaveOff(false);
    setVatOnServiceFee("");
    setValidDateError({
      validToBeforeValidFrom: false,
      promotedOutsideValidRange: false,
    });
    setShowDateErrors(false);
    setEditIndex(null);
  };

  const [loading, setLoading] = useState(false);

  const onSubmit = async (data: ServiceFeeFormInputs) => {
    // Set show errors to true to display any validation errors
    setShowDateErrors(true);

    // Validate required date fields
    if (!validFrom || !validTo) {
      dispatch(
        showSnackbar({
          message: "Both Valid From and Valid To dates are required.",
          severity: "warning",
        }),
      );
      return;
    }
    if (waveOff && (!promotedFrom || !promotedTo)) {
      dispatch(
        showSnackbar({
          message: "Both Promoted From and Promoted To dates are required.",
          severity: "warning",
        }),
      );
      return;
    }

    if (!vatOnServiceFee) {
      dispatch(
        showSnackbar({
          message: "VAT on Service Fee is required.",
          severity: "warning",
        }),
      );
      return;
    }

    // Validate date range
    if (new Date(validTo) <= new Date(validFrom)) {
      setValidDateError((prev) => ({
        ...prev,
        validToBeforeValidFrom: true,
      }));
      dispatch(
        showSnackbar({
          message: "Valid To date must be after Valid From date.",
          severity: "warning",
        }),
      );
      return;
    }

    const consolidatedRules = consolidateRuleData();

    if (consolidatedRules.length === 0) {
      dispatch(
        showSnackbar({
          message: "At least one ruleset must be defined.",
          severity: "warning",
        }),
      );
      return;
    }

    const formData = {
      ...data,
      rules: consolidatedRules,
      validFrom,
      validTo,
      waveOff,
      promotedFrom,
      promotedTo,
      vatOnServiceFee,
    };

    try {
      setLoading(true);
      let result: any;

      if (id) {
        // Update existing service fee
        result = await createAndUpdateServiceFee({
          id,
          data: {
            profileName: formData.profileName,
            profileCode: formData.profileCode,
            isActive: formData.isActive,
            description: formData.description,
            validFrom: formData.validFrom,
            validTo: formData.validTo,
            vatOnServiceFee: formData.vatOnServiceFee,
            rules: formData.rules,
            waveOff: formData.waveOff,
            promotedFrom:
              formData.waveOff === true ? formData.promotedFrom : "",
            promotedTo: formData.waveOff === true ? formData.promotedTo : "",
            // products: formData.products,
          },
        });
        if (result.error) {
          dispatch(
            showSnackbar({
              message:
                result.error.data.message || "Error in updating Service Fee.",
              severity: "error",
            }),
          );
          return;
        }

        if (result.data.status === "success") {
          dispatch(
            showSnackbar({
              message: "Service fee updated successfully",
              severity: "success",
            }),
          );
          resetForm();
          navigate("/service-fee");
        } else {
          dispatch(
            showSnackbar({
              message: "Error in updating Service Fee.",
              severity: "error",
            }),
          );
        }
      } else {
        // Create new service fee
        result = await createAndUpdateServiceFee({
          data: {
            profileName: formData.profileName,
            profileCode: formData.profileCode,
            isActive: formData.isActive,
            description: formData.description,
            validFrom: formData.validFrom,
            validTo: formData.validTo,
            vatOnServiceFee: formData.vatOnServiceFee,
            rules: formData.rules,
            waveOff: formData.waveOff,
            promotedFrom: formData.promotedFrom,
            promotedTo: formData.promotedTo,
            // products: formData.products,
          },
        });
        if (result.error) {
          dispatch(
            showSnackbar({
              message:
                result.error.data.message || "Error in adding Service Fee.",
              severity: "error",
            }),
          );
          return;
        }
        if (result.data.status === "success" || result.error.status !== 400) {
          dispatch(
            showSnackbar({
              message: result.data.message || "Service fee added successfully",
              severity: "success",
            }),
          );
          resetForm();
          navigate("/service-fee");
        } else {
          dispatch(
            showSnackbar({
              message:
                result.error.data.message || "Error in adding Service Fee.",
              severity: "error",
            }),
          );
        }
      }
    } catch (error: any) {
      const errorMessage =
        error?.error?.data?.message || "Something went wrong";
      dispatch(
        showSnackbar({
          message: errorMessage,
          severity: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string | null | undefined): string => {
    if (!date) return "";
    const d = new Date(date);
    return d.toLocaleDateString("en-CA"); // YYYY-MM-DD format
  };

  const handleEditRule = async (rule: any, index: number) => {
    setEditIndex(index);
    setRuleName(rule.ruleName);
    setPriority(rule.priority || 0);
    setOutputValue(rule.outputValue || "");
    setOutputType(rule.outputType || "");

    const fetchedModuleFields: { [key: string]: any } = {};

    // Loop through each condition and fetch module fields synchronously
    for (let i = 0; i < rule.conditions.length; i++) {
      const condition = rule.conditions[i];
      const module = condition.module;

      if (module && !fetchedModuleFields[module]) {
        try {
          const response = await fetchModuleFields(module).unwrap(); // awaits the result
          fetchedModuleFields[module] = response.data;
        } catch (err) {}
      }
    }

    setRowModuleFields((prev) => ({
      ...prev,
      ...fetchedModuleFields,
    }));

    const clonedConditions = rule.conditions?.map((cond: any) => {
      const shouldDisableValue =
        cond.module === "PaymentMethods" &&
        (cond.field === "Online" || cond.field === "BankTransfer");
      const shouldDisableOperator =
        cond.field === "Online" || cond.field === "BankTransfer";

      return {
        ...cond,
        id: Date.now() + Math.random(),
        disableValue: shouldDisableValue,
        ...(shouldDisableValue && {
          value: "",
          operator: "=",
        }),
        ...(shouldDisableOperator && { operator: "=", disableOperator: true }),
      };
    }) || [
      {
        id: Date.now(),
        module: "",
        field: "",
        value: "",
        operator: "",
        nextCondition: "",
        disableValue: false,
        disableOperator: false,
      },
    ];

    setRows(clonedConditions);
  };

  const handleSaveRule = () => {
    if (
      !ruleName ||
      !outputType ||
      !outputValue ||
      !priority
      // !vatOnServiceFee
    ) {
      dispatch(
        showSnackbar({
          message:
            "Please fill Rule Name, Priority, Output Type and Output Value",
          severity: "warning",
        }),
      );
      return;
    }

    const isRowValid = rows.every((r) => {
      const hasValue = r.disableValue ? true : !!r.value;
      return r.module && r.field && hasValue && r.operator && r.nextCondition;
    });

    if (!isRowValid) {
      dispatch(
        showSnackbar({
          message: "Please complete all ruleset fields",
          severity: "warning",
        }),
      );
      return;
    }

    // ✅ Deep clone each row with unique IDs to avoid shared references
    const clonedConditions = rows.map((r) => ({
      ...r,
      id: Date.now() + Math.random(),
    }));

    const newRule = {
      ruleName,
      priority,
      outputValue,
      outputType,
      conditions: clonedConditions,
    };

    if (editIndex !== null) {
      const updatedRules = [...rulesList];
      updatedRules[editIndex] = newRule;
      setRulesList(updatedRules);
      dispatch(
        showSnackbar({
          message: "Ruleset updated successfully",
          severity: "success",
        }),
      );
    } else {
      setRulesList((prev) => [...prev, newRule]);
      dispatch(
        showSnackbar({
          message: "Ruleset added successfully",
          severity: "success",
        }),
      );
    }

    // Reset Visual Editor state
    setRuleName("");
    setPriority(0);
    setOutputValue("");
    setOutputType("");
    // Don't reset validFrom and validTo here as they are profile-level
    setRows([
      {
        id: Date.now(),
        module: "",
        field: "",
        value: "",
        disableValue: false,
        disableOperator: false,
        operator: "",
        nextCondition: "",
      },
    ]);
    setEditIndex(null);
  };

  return (
    <Box>
      <div style={{ flexGrow: 1 }}>
        <Breadcrumb
          items={breadcrumbItems}
          name={` ${id ? "Edit" : "Add"} Service Fee Profile`}
        />
      </div>
      <Paper sx={{ p: 3 }} style={{ marginTop: "20px" }}>
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Box>
            {/* Basic Details */}
            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
              {/* Profile Name */}
              <Box sx={{ flex: 1 }}>
                <Controller
                  name="profileName"
                  control={control}
                  rules={{
                    required: "Profile name is required",
                    minLength: {
                      value: 3,
                      message: "Profile name must be at least 3 characters",
                    },
                    maxLength: {
                      value: 100,
                      message: "Profile name cannot exceed 100 characters",
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Profile Name*"
                      error={!!error}
                      onInput={(e: any) => Utils.onlyText(e)}
                      helperText={error?.message}
                    />
                  )}
                />
              </Box>

              {/* Profile Code */}
              <Box sx={{ flex: 1 }}>
                <Controller
                  name="profileCode"
                  control={control}
                  rules={{
                    required: "Profile code is required",
                    minLength: {
                      value: 3,
                      message: "Profile code must be at least 3 characters",
                    },
                    maxLength: {
                      value: 100,
                      message: "Profile code cannot exceed 100 characters",
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Code*"
                      error={!!error}
                      onInput={(e: any) => Utils.onlyText(e)}
                      helperText={error?.message}
                    />
                  )}
                />
              </Box>

              {/* Status */}
              <Box sx={{ flex: 1 }}>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      error={!!errors.isActive}
                    >
                      <InputLabel>Status*</InputLabel>
                      <Select
                        {...field}
                        value={field.value === true ? "true" : "false"} // ensure string value
                        onChange={(e) =>
                          field.onChange(e.target.value === "true")
                        } // convert string to boolean
                        label="Status"
                        sx={{ height: 44 }}
                      >
                        <MenuItem value="true">Active</MenuItem>
                        <MenuItem value="false">Inactive</MenuItem>
                      </Select>
                      {errors.isActive && (
                        <FormHelperText>
                          {errors.isActive.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Box>

              {/* Description */}
              <Box sx={{ flex: 2 }}>
                <Controller
                  name="description"
                  control={control}
                  rules={{
                    maxLength: {
                      value: 500,
                      message: "Description cannot exceed 500 characters",
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Description"
                      variant="outlined"
                      error={!!error}
                      onInput={(e: any) => Utils.onlyText(e)}
                      helperText={error?.message}
                    />
                  )}
                />
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              alignItems: "flex-start",
              mb: 3,
              "& > .field": {
                minWidth: 150, // prevent collapse too small
              },
            }}
          >
            {/* Valid From */}
            <Box sx={{ flex: "1 1 160px" }} className="field">
              <TextField
                label="Valid From*"
                type="date"
                fullWidth
                value={validFrom}
                onChange={(e) => {
                  const from = e.target.value;
                  setValidFrom(from);
                  // Only validate if both dates are present
                  if (from && validTo) {
                    setValidDateError((prev) => ({
                      ...prev,
                      validToBeforeValidFrom:
                        new Date(validTo) <= new Date(from),
                    }));
                  } else {
                    setValidDateError((prev) => ({
                      ...prev,
                      validToBeforeValidFrom: false,
                    }));
                  }
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: todayDate, // ✅ restrict to today or future dates
                }}
                error={
                  showDateErrors &&
                  (!validFrom || validDateError.validToBeforeValidFrom)
                }
                helperText={
                  showDateErrors
                    ? !validFrom
                      ? "Valid From date is required"
                      : validDateError.validToBeforeValidFrom
                        ? "Valid To must be after Valid From"
                        : ""
                    : ""
                }
                size="small"
              />
            </Box>

            {/* Valid To */}
            <Box sx={{ flex: "1 1 160px" }} className="field">
              <TextField
                label="Valid To*"
                type="date"
                fullWidth
                value={validTo}
                onChange={(e) => {
                  const to = e.target.value;
                  setValidTo(to);
                  // Only validate if both dates are present
                  if (to && validFrom) {
                    setValidDateError((prev) => ({
                      ...prev,
                      validToBeforeValidFrom:
                        new Date(to) <= new Date(validFrom),
                    }));
                  } else {
                    setValidDateError((prev) => ({
                      ...prev,
                      validToBeforeValidFrom: false,
                    }));
                  }
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: tomorrowDate, // ✅ restrict to today or future dates
                }}
                error={
                  showDateErrors &&
                  (!validTo || validDateError.validToBeforeValidFrom)
                }
                helperText={
                  showDateErrors
                    ? !validTo
                      ? "Valid To date is required"
                      : validDateError.validToBeforeValidFrom
                        ? "Valid To must be after Valid From"
                        : ""
                    : ""
                }
                size="small"
              />
            </Box>

            {/* Wave off Promotion */}
            <Box
              sx={{ flex: "0 0 auto", display: "flex", alignItems: "center" }}
              className="field"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={waveOff}
                    onChange={(e) => {
                      setWaveOff(e.target.checked);
                      setPromotedFrom("");
                      setPromotedTo("");
                    }}
                    size="small"
                  />
                }
                label="Wave off Promotion?"
                sx={{ m: 0, pl: 0.5 }}
              />
            </Box>

            {/* Promoted From / To when waveOff is true */}
            {waveOff && (
              <>
                <Box sx={{ flex: "1 1 140px" }} className="field">
                  <TextField
                    label="Promoted From*"
                    type="date"
                    fullWidth
                    value={promotedFrom}
                    onChange={(e) => {
                      const from = e.target.value;
                      setPromotedFrom(from);
                      if (
                        promotedTo &&
                        new Date(promotedTo) <= new Date(from)
                      ) {
                        setPromotedDateError(true);
                      } else {
                        setPromotedDateError(false);
                      }
                    }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      min: todayDate, // ✅ restrict to today or future dates
                    }}
                    error={
                      showPromotedDateErrors &&
                      (!promotedFrom || promotedDateError)
                    }
                    helperText={
                      showPromotedDateErrors && !promotedFrom
                        ? "Promoted From date is required"
                        : showPromotedDateErrors && promotedDateError
                          ? "Promoted To must be after Promoted From"
                          : ""
                    }
                    size="small"
                  />
                </Box>
                <Box sx={{ flex: "1 1 140px" }} className="field">
                  <TextField
                    label="Promoted To*"
                    type="date"
                    fullWidth
                    value={promotedTo}
                    onChange={(e) => {
                      const to = e.target.value;
                      setPromotedTo(to);
                      if (
                        promotedFrom &&
                        new Date(to) <= new Date(promotedFrom)
                      ) {
                        setPromotedDateError(true);
                      } else {
                        setPromotedDateError(false);
                      }
                    }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      min: tomorrowDate, // ✅ restrict to today or future dates
                    }}
                    error={
                      showPromotedDateErrors &&
                      (!promotedTo || promotedDateError)
                    }
                    helperText={
                      showPromotedDateErrors && !promotedTo
                        ? "Promoted To date is required"
                        : showPromotedDateErrors && promotedDateError
                          ? "Promoted To must be after Promoted From"
                          : ""
                    }
                    size="small"
                  />
                </Box>
              </>
            )}

            {/* VAT on Service Fee */}
            <Box sx={{ flex: "1 1 160px" }} className="field">
              <FormControl fullWidth size="small">
                <TextField
                  label="VAT on Service Fee (%)*"
                  type="number"
                  placeholder="Enter VAT on Service Fee (%)"
                  value={vatOnServiceFee}
                  onChange={(e) => setVatOnServiceFee(e.target.value)}
                  InputProps={{ inputProps: { min: 0, max: 100, step: 0.01 } }}
                  sx={{ height: "45px" }}
                  size="small"
                />
              </FormControl>
            </Box>
          </Box>

          {/* Rules Editor Container */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              gap: 2,
            }}
          >
            {/* Visual Editor (Left Panel) */}
            <Box
              sx={{
                width: "50%",
                border: 1,
                borderColor: "grey.300",
                borderRadius: 2,
                boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
                p: 2,
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                Visual Editor
              </Typography>
              {/* Rule name and Priority */}
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <TextField
                  label="Rule name*"
                  fullWidth
                  value={ruleName}
                  onInput={(e: any) => Utils.onlyText(e)}
                  onChange={(e) => setRuleName(e.target.value)}
                />
                <TextField
                  label="Priority*"
                  type="number"
                  fullWidth
                  value={priority}
                  onChange={(e) => setPriority(Number(e.target.value))}
                  inputProps={{ min: 0 }}
                />
              </Box>
              <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                Conditions
              </Typography>
              {/* Dynamic Rule Rows */}
              {rows.map((row, index) => (
                <Box
                  key={row.id}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  sx={{ mb: 2, width: "100%" }}
                >
                  <FormControl fullWidth size="small" sx={{ flex: 1 }}>
                    <InputLabel>Module*</InputLabel>
                    <Select
                      value={row.module}
                      label="Selected Module*"
                      onChange={(e) =>
                        handleRowChange(row.id, "module", e.target.value)
                      }
                    >
                      {modules.map((mod) => (
                        <MenuItem key={mod} value={mod}>
                          {mod}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small" sx={{ flex: 1 }}>
                    <InputLabel>Fields*</InputLabel>
                    <Select
                      value={row.field}
                      label="Selected list Of fields*"
                      onChange={(e) =>
                        handleRowChange(row.id, "field", e.target.value)
                      }
                    >
                      {getRowModuleFields(row.module).map((field: any) => (
                        <MenuItem key={field} value={field}>
                          {field}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {!row.disableValue && (
                    <TextField
                      label="Value*"
                      size="small"
                      type="number"
                      fullWidth
                      sx={{ flex: 1 }}
                      value={row.value}
                      inputProps={{ min: 0, step: 0.01 }}
                      onChange={(e) => {
                        if (row.disableValue) {
                          handleRowChange(row.id, "value", null);
                        } else {
                          handleRowChange(row.id, "value", e.target.value);
                        }
                      }}
                      disabled={row.disableValue}
                    />
                  )}

                  {row.disableOperator ? (
                    <TextField
                      label="Operator*"
                      size="small"
                      fullWidth
                      sx={{ flex: 1 }}
                      value="="
                      disabled
                    />
                  ) : (
                    <FormControl fullWidth size="small" sx={{ flex: 1 }}>
                      <InputLabel>Operator*</InputLabel>
                      <Select
                        value={row.operator}
                        label="Selected operator*"
                        onChange={(e) =>
                          handleRowChange(row.id, "operator", e.target.value)
                        }
                      >
                        {operators.map((op) => (
                          <MenuItem key={op} value={op}>
                            {op}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  <FormControl fullWidth size="small" sx={{ flex: 1 }}>
                    <InputLabel>Nextcondition*</InputLabel>
                    <Select
                      value={row.nextCondition}
                      label="Selected Nextcondition*"
                      onChange={(e) =>
                        handleRowChange(row.id, "nextCondition", e.target.value)
                      }
                    >
                      {nextConditions.map((nc) => (
                        <MenuItem key={nc} value={nc}>
                          {nc}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box display="flex" alignItems="center" gap={1}>
                    {index === rows.length - 1 && (
                      <IconButton onClick={addRow} color="secondary">
                        <FaPlus size={16} />
                      </IconButton>
                    )}
                    {rows.length > 1 && (
                      <IconButton
                        onClick={() => removeRow(row.id)}
                        color="error"
                      >
                        <FaMinus size={16} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              ))}

              {/* Output Section */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                  Output value
                </Typography>
                <Box display="flex" gap={2} mb={2}>
                  <TextField
                    type="number"
                    fullWidth
                    placeholder="Enter value in (%)"
                    size="small"
                    label="Value (%)*"
                    value={outputValue}
                    // onInput={(e: any) => Utils.onlyText(e)}
                    InputProps={{
                      inputProps: { min: 0, max: 100, step: 0.01 },
                    }}
                    onChange={(e) => setOutputValue(e.target.value)}
                  />
                  <FormControl fullWidth size="small">
                    <InputLabel>Type*</InputLabel>
                    <Select
                      value={outputType}
                      label="Type"
                      onChange={(e) => setOutputType(e.target.value)}
                      sx={{ height: "45px" }}
                    >
                      <MenuItem value="Flat">Flat</MenuItem>
                      <MenuItem value="Percentage">Percentage</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box display="flex" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveRule}
                    disabled={isLoading}
                    sx={{
                      width: "150px",
                      height: "40px",
                      borderRadius: "8px",
                      textTransform: "none",
                      fontSize: "14px",
                      fontWeight: "600",
                      backgroundColor: "#2196F3",
                      "&:hover": {
                        backgroundColor: "#1976D2",
                      },
                    }}
                  >
                    {editIndex !== null ? "Save Rule" : "Add Rule"}
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Rule View (Right Panel) */}
            <Box
              sx={{
                width: "50%",
                border: 1,
                borderColor: "grey.300",
                borderRadius: 2,
                boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
                p: 2,
              }}
            >
              {/* Fixed Header */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Rule View
                </Typography>
                {rulesList.length > 0 && (
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: "bold",
                      color: "grey.600",
                      fontSize: "16px",
                    }}
                  >
                    Rules ({rulesList.length})
                  </Typography>
                )}
              </Box>

              {/* Scrollable Rule Cards */}
              {rulesList.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "220px",
                  }}
                >
                  <Typography color="text.secondary" align="center">
                    No ruleset added yet.
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    maxHeight: 300,
                    overflowY: "auto",
                    pr: 1,
                  }}
                >
                  {rulesList.map((rule, i) => {
                    const isEditing = editIndex === i;

                    return (
                      <Paper
                        key={i}
                        variant="outlined"
                        sx={{
                          mb: 2,
                          p: 2,
                          pt: 2.5,
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        {/* Header */}
                        <Box
                          display="flex"
                          alignItems="flex-start"
                          justifyContent="space-between"
                          mb={2}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              color: "black",
                              fontWeight: "bold",
                              fontSize: "15px",
                              pr: 6, // space for corner close icon
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                              lineHeight: 1.35,
                              flex: 1,
                            }}
                          >
                            {rule.ruleName} | Priority: {rule.priority || "N/A"}{" "}
                            | Type: {rule.outputType} | Output:{" "}
                            {rule.outputValue}
                          </Typography>

                          {/* Remove Button */}
                          <IconButton
                            onClick={() =>
                              setRulesList((prev) =>
                                prev.filter((_, idx: number) => idx !== i),
                              )
                            }
                            color="error"
                            size="small"
                            sx={{ ml: 1, alignSelf: "flex-start" }}
                          >
                            <FaXmark size={16} />
                          </IconButton>
                        </Box>

                        {/* Conditions Table */}
                        <Box sx={{ overflowX: "auto" }}>
                          <Table
                            size="small"
                            sx={{ tableLayout: "fixed", width: "100%" }}
                          >
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ width: "20%" }}>
                                  Module
                                </TableCell>
                                <TableCell sx={{ width: "20%" }}>
                                  Field
                                </TableCell>
                                <TableCell sx={{ width: "20%" }}>
                                  Value
                                </TableCell>
                                <TableCell sx={{ width: "20%" }}>
                                  Operator
                                </TableCell>
                                <TableCell sx={{ width: "20%" }}>
                                  Next Condition
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {rule.conditions.map(
                                (cond: any, index: number) => (
                                  <TableRow key={index}>
                                    {[
                                      "module",
                                      "field",
                                      "value",
                                      "operator",
                                      "nextCondition",
                                    ].map((key) => (
                                      <TableCell
                                        key={key}
                                        sx={{
                                          whiteSpace: "normal",
                                          wordBreak: "break-word",
                                        }}
                                      >
                                        {key === "value" &&
                                        (cond[key] === "" ||
                                          cond[key] === null ||
                                          cond[key] === undefined)
                                          ? "-"
                                          : cond[key]}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                ),
                              )}
                            </TableBody>
                          </Table>
                        </Box>

                        {/* Edit Button */}
                        <Box display="flex" justifyContent="flex-end" mt={2}>
                          <Button
                            variant="outlined"
                            color="primary"
                            disabled={isEditing}
                            onClick={() => handleEditRule(rule, i)}
                          >
                            Edit
                          </Button>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Box>

          {/* Save and Cancel Buttons */}
          <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                <span>Save Service Fee Profile</span>
              )}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate("/service-fee")}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default AddServiceFee;
